/**
 * Keycloak authentication module.
 *
 * Responsibilities:
 *  - Initialize a Keycloak client using environment configuration.
 *  - Maintain in-memory auth state (token, parsed token, refresh status, errors).
 *  - Proactively refresh tokens before expiry and reactively after expiry.
 *  - Expose imperative auth actions (login, logout, account management, manual refresh).
 *  - Synchronize a global token store via setToken / clearToken.
 *
 * NOTE: React context/provider removed; module now exports init and auth helper functions.
 */

import Keycloak from 'keycloak-js';
import type {KeycloakInitOptions, KeycloakTokenParsed, KeycloakConfig} from 'keycloak-js';
import {isDebug} from '../base/utils/GeneralUtilities';
import {setToken, clearToken} from './tokenStore';

// Inferred Keycloak client type (replacement for deprecated KeycloakInstance)
type KeycloakClient = InstanceType<typeof Keycloak>;

// Module internal state
/** Active Keycloak client instance after successful initialization; undefined until init completes or after disposal. */
let keycloakClient: KeycloakClient | undefined;
/** Indicates that initialization (or debug bypass) has finished. */
let initialized = false;
/** True while a refresh operation (manual, auto-expired, pre-expiry) is in progress to prevent overlaps. */
let refreshing = false;
/** Holds last authentication / refresh related error message, if any. Cleared on successful token updates. */
let authError: string | undefined;
/** Timeout id for scheduled pre-expiry refresh; cleared on logout, disposal, or rescheduling. */
let preExpiryTimeoutId: number | undefined;


/**
 * Applies the latest token and parsed claims from a Keycloak client to the global token store.
 * Centralizes token propagation to avoid duplicated state-setting logic across refresh paths.
 *
 * @param kc Keycloak client whose current token/tokenParsed will be copied into the global store.
 */
function applyTokenFromKeycloak(kc: KeycloakClient) {
    setToken(kc.token ?? undefined, kc.tokenParsed as KeycloakTokenParsed | undefined);
}


/**
 * Schedules a proactive token refresh 30 seconds before the access token expiry.
 * If the remaining lifetime is shorter than the lead window, no timer is scheduled and the
 * reactive onTokenExpired handler will handle refresh instead.
 *
 * Resets any existing timer before scheduling a new one to ensure only a single future refresh is queued.
 *
 * @param kc Keycloak client containing a parsed token with exp claim (seconds since epoch).
 */
function schedulePreExpiryRefresh(kc: KeycloakClient) {
    if (!kc.tokenParsed?.exp) return;
    if (preExpiryTimeoutId) window.clearTimeout(preExpiryTimeoutId);
    const leadMs = 30000; // refresh 30s before expiry
    const msUntilLead = kc.tokenParsed.exp * 1000 - Date.now() - leadMs;
    if (msUntilLead <= 0) return; // too close; rely on onTokenExpired
    preExpiryTimeoutId = window.setTimeout(() => {
        performTokenRefresh(kc, 30, 'pre-expiry');
    }, msUntilLead);
}


/**
 * Performs a token refresh using Keycloak's updateToken API.
 * Guards against concurrent refresh attempts via the module-level 'refreshing' flag.
 *
 * Success Path:
 *  - Updates token and parsed claims (applyTokenFromKeycloak)
 *  - Re-schedules pre-expiry refresh
 *  - Clears any existing auth error
 *
 * Failure Path:
 *  - Logs a descriptive error
 *  - Sets authError (prefixed by origin)
 *  - Clears token store unless origin is 'pre-expiry' (a non-critical early attempt)
 *
 * @param kc Active Keycloak client instance
 * @param minValiditySeconds Desired minimum remaining validity after refresh attempt
 * @param origin Source classification of the refresh trigger (manual|auto-expired|pre-expiry)
 */
function performTokenRefresh(kc: KeycloakClient, minValiditySeconds: number, origin: 'manual' | 'auto-expired' | 'pre-expiry') {
    if (refreshing) return; // guard concurrency
    refreshing = true;
    kc.updateToken(minValiditySeconds).then(refreshed => {
        if (refreshed) {
            applyTokenFromKeycloak(kc);
            schedulePreExpiryRefresh(kc);
            authError = undefined;
        } else if (origin === 'auto-expired') {
            authError = 'Auto refresh failed';
            clearToken();
        }
    }).catch(err => {
        console.error(`Token ${origin} refresh failed`, err);
        authError = `${origin} refresh failed`;
        if (origin !== 'pre-expiry') clearToken();
    }).finally(() => {
        refreshing = false;
    });
}

/**
 * Initializes the Keycloak authentication layer (idempotent).
 *
 * Sequence:
 *  1. Skips initialization entirely in debug mode (clears token, marks initialized).
 *  2. Constructs a Keycloak client from environment configuration.
 *  3. Performs a silent SSO check (onLoad: 'check-sso').
 *  4. If authenticated: stores token + schedules a proactive pre-expiry refresh.
 *     If not authenticated: clears any token state (user remains unauthenticated without redirect).
 *  5. Registers event handlers for token expiry, logout, and refresh outcomes.
 *
 * Idempotency: repeated calls after successful initialization are no-ops.
 *
 * Error Handling:
 *  - Any init exception logs to console, sets an auth error message, and clears token state.
 *
 * Side Effects:
 *  - Mutates module-level state (keycloakClient, initialized, authError).
 *  - Writes token + claims into the global token store via setToken / clearToken.
 *
 * @returns Promise that resolves once initialization attempt finishes (success, failure, or debug bypass).
 */
export async function initKeycloakAuth(): Promise<void> {
    if (initialized) return;
    if (isDebug()) {
        clearToken();
        initialized = true;
        return;
    }
    const config: KeycloakConfig = {
        url: import.meta.env.VITE_KEYCLOAK_URL,
        realm: import.meta.env.VITE_KEYCLOAK_REALM,
        clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
    };
    const kc = new Keycloak(config);
    keycloakClient = kc;

    const initOptions: KeycloakInitOptions = {
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
        pkceMethod: 'S256',
        checkLoginIframe: true,
        enableLogging: false,
    };

    try {
        const authenticated = await kc.init(initOptions);
        if (authenticated) {
            applyTokenFromKeycloak(kc);
            schedulePreExpiryRefresh(kc);
        } else {
            clearToken();
        }
    } catch (e) {
        console.error('Keycloak init failed', e);
        authError = 'Initialization failed';
        clearToken();
    }
    initialized = true;

    // Event handlers
    kc.onTokenExpired = () => performTokenRefresh(kc, 30, 'auto-expired');
    kc.onAuthLogout = () => {
        clearToken();
        if (preExpiryTimeoutId) window.clearTimeout(preExpiryTimeoutId);
    };
    kc.onAuthRefreshError = () => {
        authError = 'Refresh error';
    };
    kc.onAuthRefreshSuccess = () => {
        authError = undefined;
        applyTokenFromKeycloak(kc);
        schedulePreExpiryRefresh(kc);
    };
    kc.onAuthSuccess = () => {
        authError = undefined;
        applyTokenFromKeycloak(kc);
        schedulePreExpiryRefresh(kc);
    };
}

/**
 * Initiates interactive login via Keycloak (opens redirect / popup depending on config).
 * Returns the Promise from the Keycloak adapter or undefined if the client is not initialized yet.
 */
export const login = () => keycloakClient?.login();

/**
 * Initiates logout from Keycloak. On success Keycloak will trigger onAuthLogout handler
 * which clears the in-memory token and any scheduled refresh timer.
 * Returns the underlying Promise or undefined if the client is not initialized.
 */
export const logout = () => keycloakClient?.logout();

/**
 * Opens Keycloak account management (if enabled for the realm/client). Returns a Promise
 * or undefined if the client is not initialized.
 */
export const account = () => keycloakClient?.accountManagement();

/**
 * Indicates whether the user is considered authenticated.
 * True only if not in debug mode and a non-empty access token is present.
 * Note: Does not proactively check token expiry; near-expiry tokens may still return true.
 */
export function isAuthenticated(): boolean {
    return !isDebug() && !!keycloakClient?.token;
}

/**
 * Retrieves the latest authentication / refresh error message if any.
 * Cleared on successful token refresh or auth success events.
 */
export function getAuthError(): string | undefined {
    return authError;
}

/**
 * Indicates whether initial Keycloak initialization (or debug bypass) has completed.
 * Until this is true, token-related accessors may return undefined.
 */
export function isAuthInitialized(): boolean {
    return initialized;
}

/**
 * Returns whether a token refresh operation is currently in progress.
 * Useful to disable UI actions that would trigger overlapping refresh attempts.
 */
export function isRefreshing(): boolean {
    return refreshing;
}

/**
 * Provides direct access to the underlying Keycloak client instance for advanced operations
 * (e.g. inspecting tokenParsed, initiating custom flows). Should be used read-only; side effects
 * may interfere with this module's refresh orchestration.
 */
export function getKeycloakClient(): KeycloakClient | undefined {
    return keycloakClient;
}

/**
 * Disposes Keycloak authentication state managed by this module:
 *  - Clears any scheduled pre-expiry refresh timeout.
 *  - Resets internal flags (initialized, refreshing, authError).
 *  - Clears the global token store.
 *
 * Typically, not required for standard SPA lifecycle; provided for testing or forced re-init scenarios.
 */
export function disposeKeycloakAuth(): void {
    if (preExpiryTimeoutId) window.clearTimeout(preExpiryTimeoutId);
    preExpiryTimeoutId = undefined;
    keycloakClient = undefined;
    refreshing = false;
    initialized = false;
    authError = undefined;
    clearToken();
}
